import React from 'react';
import { Helmet } from 'react-helmet-async';

interface Props {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string[];
  author?: string;
}

export default function SEO({ 
  title = 'Make Resume Free - Professional Resume Builder',
  description = 'Create professional, ATS-friendly resumes in minutes with our easy-to-use resume builder. Free forever, no sign-up required.',
  image = 'https://makeresume.free/og-image.jpg',
  url = 'https://makeresume.free',
  type = 'website',
  keywords = [
    'resume builder',
    'CV maker',
    'professional resume',
    'job application',
    'career',
    'ATS-friendly',
    'free resume builder',
    'online resume',
    'resume template',
    'resume generator'
  ],
  author = 'Make Resume Free'
}: Props) {
  const siteTitle = title.includes('Make Resume Free') ? title : `${title} | Make Resume Free`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Make Resume Free" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@resbuild" />
      <meta name="twitter:creator" content="@resbuild" />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="theme-color" content="#0d9488" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="ResBuild" />
      <meta name="application-name" content="ResBuild" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="msapplication-TileColor" content="#0d9488" />
      <meta name="msapplication-config" content="/browserconfig.xml" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "ResBuild",
          "url": "https://resbuild.com",
          "description": description,
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "All",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "ResBuild",
            "url": "https://resbuild.com"
          }
        })}
      </script>
    </Helmet>
  );
}